import React, { forwardRef } from "react";

import { cn, cva, platformProps, triggerHaptic } from "../../lib";
import { minTouchTargetClassName } from "../../lib/touch-target";
import { Pressable } from "../../primitives";
import { Icon } from "../icons";
import { Typography } from "../typography";
import {
  chipHitSlop,
  chipIconColorClass,
  chipIconSize,
  chipIconStateClass,
  chipTextColor,
  chipTextStateClass,
  chipTypographyVariant,
  getChipAccessibilityLabel,
  getChipAccessibilityState,
} from "./lib";
import type { ChipProps } from "./types";

const chipVariants = cva(
  [
    "rounded-radius-full group relative inline-flex max-w-full flex-row items-center justify-center overflow-hidden border border-solid outline-none transition-colors disabled:cursor-not-allowed",
    // Keyboard focus on web, from the shared focus-ring tokens
    "web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-focus web:focus-visible:ring-offset-focus",
    // Touch screens on web: a tap area of at least 44px (native: `chipHitSlop`),
    // which the root's own clip would otherwise cut off
    minTouchTargetClassName,
    "[@media(pointer:coarse)]:overflow-visible",
  ],
  {
    variants: {
      selected: {
        false: "bg-luce-neutral-background-default border-luce-neutral-border-mediumEmphasis",
        true: "bg-luce-button-cta2-surfaceHover border-luce-button-cta2-border",
      },
      // Heights: Figma's `_button/height`, as Button's. Figma's padding-md/lg
      // are 12/16 on mobile and 16/24 on desktop. The spacing classes hold
      // fixed values, so desktop picks the class with Figma's desktop value.
      size: {
        sm: "gap-spacing-sm px-spacing-md md:px-spacing-lg h-button-sm md:h-button-sm-dt",
        md: "gap-spacing-sm px-spacing-lg md:px-spacing-xl h-button-md md:h-button-md-dt",
        lg: "gap-spacing-sm px-spacing-lg md:px-spacing-xl h-button-lg md:h-button-lg-dt",
      },
      // Figma's button/disabled group (designer feedback, 2026-10-28)
      disabled: {
        true: "bg-luce-button-disabled-ctaDisabled border-luce-button-disabled-ctaBorderDisabled",
        false: "",
      },
    },
    // Hover and press only when enabled: a disabled <button> still matches :hover
    compoundVariants: [
      {
        selected: false,
        disabled: false,
        class:
          "hover:bg-luce-neutral-background-intermediate hover:border-luce-neutral-border-highEmphasis active:bg-luce-neutral-background-attenuated active:border-luce-neutral-border-highEmphasis",
      },
      {
        selected: true,
        disabled: false,
        // `active:` repeats the hover border: touch has no hover, so native pressed needs its own
        class:
          "hover:border-luce-button-cta2-borderHover active:border-luce-button-cta2-borderHover active:bg-luce-button-cta2-surfacePressed",
      },
    ],
    defaultVariants: {
      selected: false,
      size: "md",
      disabled: false,
    },
  },
);

export const Chip = forwardRef<React.ComponentRef<typeof Pressable>, ChipProps>(
  (
    {
      children,
      selected: selectedProp,
      size = "md",
      disabled = false,
      onClick,
      leadingIcon,
      trailingIcon,
      iconWeight = "regular",
      className,
      testID,
      hapticFeedback = "light",
      hitSlop = chipHitSlop[size],
      ...pressableProps
    },
    ref,
  ) => {
    const { accessibilityLabel, accessibilityState, ...restPressableProps } = pressableProps;
    const selected = selectedProp ?? false;
    const variantKey = selected ? "selected" : "default";
    const isDisabled = disabled;

    const accessibilityContext = {
      accessibilityLabel,
      accessibilityState,
      children,
      selected: selectedProp,
      isDisabled,
    };

    const resolvedAccessibilityLabel = getChipAccessibilityLabel(accessibilityContext);
    const mergedAccessibilityState = getChipAccessibilityState(accessibilityContext);

    const handleClick: ChipProps["onClick"] = (event) => {
      triggerHaptic(hapticFeedback);
      onClick?.(event);
    };

    return (
      <Pressable
        ref={ref}
        {...restPressableProps}
        // Only a chip given `selected` is a toggle; an action chip stays a plain
        // button. A chip that discloses something (`accessibilityState.expanded`,
        // e.g. a menu trigger) isn't a toggle either: its `selected` is a look.
        {...platformProps({
          testID,
          slot: "chip",
          pressed: accessibilityState?.expanded == null ? selectedProp : undefined,
        })}
        accessibilityRole="button"
        accessibilityLabel={resolvedAccessibilityLabel}
        accessibilityState={mergedAccessibilityState}
        disabled={isDisabled}
        hitSlop={hitSlop}
        onClick={handleClick}
        className={cn(chipVariants({ selected, size, disabled: isDisabled }), className)}
      >
        {leadingIcon && (
          <Icon
            name={leadingIcon}
            size={chipIconSize[size]}
            weight={iconWeight}
            className={cn(
              "shrink-0",
              chipIconColorClass[variantKey][isDisabled ? "disabled" : "default"],
              !isDisabled && chipIconStateClass[variantKey],
            )}
          />
        )}

        <Typography
          variant={chipTypographyVariant[size]}
          color={chipTextColor[variantKey][isDisabled ? "disabled" : "default"]}
          whiteSpace="nowrap"
          className={cn(
            "max-w-full shrink truncate",
            !isDisabled && chipTextStateClass[variantKey],
          )}
        >
          {children}
        </Typography>

        {trailingIcon && (
          <Icon
            name={trailingIcon}
            size={chipIconSize[size]}
            weight={iconWeight}
            className={cn(
              "shrink-0",
              chipIconColorClass[variantKey][isDisabled ? "disabled" : "default"],
              !isDisabled && chipIconStateClass[variantKey],
            )}
          />
        )}
      </Pressable>
    );
  },
);

Chip.displayName = "Chip";
